import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, LogOut, Activity, AlertCircle, Users, CheckCircle } from 'lucide-react';
import apiClient from '../services/api';

/**
 * ActiveSessions — admin screen to view logged-in users and force logout.
 *
 * Backend endpoints:
 *   GET  /active-sessions.php   -> list of session-holding users
 *   POST /force-logout.php      -> { userid } kicks that user
 *
 * Add a route (e.g. 'active-sessions') pointing at this component and a
 * menu entry visible to admins.
 */
const ActiveSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [kickingId, setKickingId] = useState(null);

  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await apiClient.get('/active-sessions.php');
      if (res.data.success) {
        setSessions(res.data.data?.sessions || []);
      } else {
        setError(res.data.message || 'Failed to load active sessions');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load active sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const handleForceLogout = async (user) => {
    const ok = window.confirm(
      `Force logout "${user.fullname || user.username}"?\n\nTheir session will end within a minute and they will be able to log in again immediately.`
    );
    if (!ok) return;

    try {
      setKickingId(user.userid);
      setMessage('');
      setError('');
      const res = await apiClient.post('/force-logout.php', { userid: user.userid });
      if (res.data.success) {
        setMessage(res.data.message);
        await loadSessions();
      } else {
        setError(res.data.message || 'Force logout failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Force logout failed');
    } finally {
      setKickingId(null);
    }
  };

  const formatIdle = (seconds) => {
    if (seconds == null) return '—';
    if (seconds < 60) return `${seconds}s ago`;
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m ago`;
  };

  return (
    <div className="p-6 lg:p-8 min-h-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Users size={24} className="text-[#2563EB]" />
          <h2 className="text-[1.4rem] text-[#1E293B] font-semibold m-0">Active Sessions</h2>
        </div>
        <button
          onClick={loadSessions}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 border border-[#E2E8F0] rounded-lg
            text-gray-700 font-medium cursor-pointer transition-all duration-200
            hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Success message */}
      {message && (
        <div className="flex items-center gap-3 px-4 py-3 mb-5 bg-green-50 border border-green-300 border-l-4 border-l-green-600 rounded-lg">
          <CheckCircle size={18} className="text-green-700 flex-shrink-0" />
          <span className="text-[14px] font-medium text-green-800">{message}</span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 mb-5 bg-red-50 border border-red-300 border-l-4 border-l-red-600 rounded-lg">
          <AlertCircle size={18} className="text-red-700 flex-shrink-0" />
          <span className="text-[14px] font-medium text-red-800">{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <Activity size={32} className="text-[#2563EB] animate-spin" />
            <p className="text-gray-500 text-sm">Loading active sessions...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-[#F1F5F9] border-b-2 border-[#E2E8F0]">
                <tr>
                  {['User', 'Full Name', 'Type', 'Last Activity', 'Status', 'Action'].map(h => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-gray-700 text-[0.9rem] uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sessions.length > 0 ? sessions.map(s => (
                  <tr key={s.userid} className="border-b border-[#E2E8F0] transition-colors duration-150 hover:bg-[#EFF6FF]">
                    <td className="px-4 py-4 font-semibold text-[#2563EB]">{s.username}</td>
                    <td className="px-4 py-4 text-gray-700">{s.fullname || '—'}</td>
                    <td className="px-4 py-4 text-gray-700">{s.usertype || '—'}</td>
                    <td className="px-4 py-4 text-gray-700">{formatIdle(s.idle_seconds)}</td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-[0.82rem] font-medium ${
                          s.is_live
                            ? 'bg-[#DCFCE7] text-[#15803D]'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {s.is_live ? 'Online' : 'Stale'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => handleForceLogout(s)}
                        disabled={kickingId === s.userid}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-white text-[0.85rem] font-semibold
                          border-0 cursor-pointer transition-colors duration-200
                          disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ backgroundColor: '#DC2626' }}
                        onMouseEnter={(e) => { if (kickingId !== s.userid) e.currentTarget.style.backgroundColor = '#B91C1C'; }}
                        onMouseLeave={(e) => { if (kickingId !== s.userid) e.currentTarget.style.backgroundColor = '#DC2626'; }}
                      >
                        <LogOut size={14} />
                        {kickingId === s.userid ? 'Logging out...' : 'Force Logout'}
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="text-center py-10 text-gray-500">
                      No active sessions
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info note */}
      <div className="flex items-start gap-3 p-4 mt-5 bg-[#F8FAFC] border-l-4 border-[#2563EB] rounded-lg">
        <AlertCircle size={18} className="text-[#2563EB] flex-shrink-0 mt-0.5" />
        <p className="text-gray-600 text-[0.9rem] m-0 leading-relaxed">
          Force logout ends the user's session within about a minute (their next heartbeat)
          and immediately frees the slot so they can log in again from any device.
        </p>
      </div>
    </div>
  );
};

export default ActiveSessions;